import { getColors } from '../../charts/shared/palettes';
import { J, JN, TITLE_SPACING, fileHeader } from './shared';

export function genBoxPlot(chartType, config, t, data) {
  const colors = getColors(config, data.length, t);

  const allVals = data.flatMap(d => [d.min, d.max, ...(d.outliers || [])]);
  const yMin = (config.yMin && String(config.yMin).trim() !== '') ? parseFloat(config.yMin) : Math.floor(Math.min(...allVals) - 5);
  const yMax = (config.yMax && String(config.yMax).trim() !== '') ? parseFloat(config.yMax) : Math.ceil(Math.max(...allVals) + 5);
  const boxWidthRatio = config.boxWidth || 0.4;
  const showGrid = !!config.showGrid;
  const showOutliers = !!config.showOutliers;
  const hasTitle = !!(config.title && String(config.title).trim());
  const topMargin = hasTitle ? 40 : 20;

  const outliersBlock = showOutliers
    ? `              {(item.outliers || []).map((outlier, oIdx) => (
                <text key={'o-' + idx + '-' + oIdx} x={centerX} y={yScale(outlier)} textAnchor="middle" dominantBaseline="middle" fontSize={16} fontWeight="bold" fill={THEME.foreground}>*</text>
              ))}`
    : '';

  return (
    fileHeader(['@visx/scale', '@visx/group', '@visx/grid', '@visx/axis'], 'box-plot') +
    `import React, { useMemo } from 'react';
import { scaleBand, scaleLinear } from '@visx/scale';
import { Group } from '@visx/group';
import { GridRows } from '@visx/grid';
import { AxisLeft, AxisBottom } from '@visx/axis';

const DATA = ${JN(data)};
const COLORS = ${J(colors)};
const THEME = ${JN(t)};
const Y_DOMAIN = [${yMin}, ${yMax}];
const BOX_WIDTH_RATIO = ${boxWidthRatio};

export default function Chart({ width = 720, height = 480 }) {
  const margin = { top: ${topMargin}, right: 80, bottom: 60, left: 70 };
  const innerWidth = width - margin.left - margin.right;
  const innerHeight = height - margin.top - margin.bottom;

  const xScale = useMemo(() => scaleBand({
    domain: DATA.map(d => d.category),
    range: [0, innerWidth],
    padding: 0,
  }), [innerWidth]);

  const yScale = useMemo(() => scaleLinear({
    domain: Y_DOMAIN,
    range: [innerHeight, 0],
  }), [innerHeight]);

  const tickCount = 5;
  const yTicks = useMemo(() => {
    const step = (Y_DOMAIN[1] - Y_DOMAIN[0]) / (tickCount - 1);
    return Array.from({ length: tickCount }, (_, i) => Math.round(Y_DOMAIN[0] + step * i));
  }, []);

  const boxWidth = xScale.bandwidth() * BOX_WIDTH_RATIO;

  return (
    <div style={{ width, height, position: 'relative', backgroundColor: THEME.background }}>
      ${hasTitle ? `<div style={{ position: 'absolute', top: ${TITLE_SPACING.topInset}, left: 70, fontSize: 18, fontWeight: 400, color: THEME.titleColor }}>${String(config.title).replace(/</g, '&lt;')}</div>` : ''}
      <svg width={width} height={height}>
        <Group left={margin.left} top={margin.top}>
          ${showGrid ? `<GridRows scale={yScale} width={innerWidth} stroke={THEME.gridColor} strokeOpacity={THEME.gridAlpha} strokeDasharray="3 3" tickValues={yTicks} />` : ''}
          <AxisLeft
            scale={yScale}
            stroke={THEME.axisColor}
            strokeWidth={THEME.spineWidth}
            tickStroke={THEME.axisColor}
            tickLength={5}
            tickValues={yTicks}
            tickLabelProps={() => ({ fill: THEME.tickColor, fontSize: 12, textAnchor: 'end', dx: -4, dy: '0.33em' })}
          />
          <AxisBottom
            scale={xScale}
            top={innerHeight}
            stroke={THEME.axisColor}
            strokeWidth={THEME.spineWidth}
            tickStroke={THEME.axisColor}
            tickLength={5}
            tickLabelProps={() => ({ fill: THEME.tickColor, fontSize: 12, textAnchor: 'middle', dy: 10 })}
          />
          <text x={-innerHeight / 2} y={-(margin.left - 20)} transform="rotate(-90)" textAnchor="middle" fontSize={14} fill={THEME.labelColor}>${config.ylabel || ''}</text>
          <text x={innerWidth / 2} y={innerHeight + margin.bottom - 15} textAnchor="middle" fontSize={14} fill={THEME.labelColor}>${config.xlabel || ''}</text>
          {DATA.map((item, idx) => {
            const centerX = (xScale(item.category) || 0) + xScale.bandwidth() / 2;
            const color = COLORS[idx % COLORS.length];
            const minY = yScale(item.min);
            const maxY = yScale(item.max);
            const q1Y = yScale(item.q1);
            const q3Y = yScale(item.q3);
            const medianY = yScale(item.median);
            const halfBox = boxWidth / 2;
            const capWidth = boxWidth / 3;
            return (
              <g key={'box-' + idx}>
                <line x1={centerX} y1={minY} x2={centerX} y2={q1Y} stroke={THEME.foreground} strokeWidth={1.5} />
                <line x1={centerX} y1={q3Y} x2={centerX} y2={maxY} stroke={THEME.foreground} strokeWidth={1.5} />
                <line x1={centerX - capWidth} y1={minY} x2={centerX + capWidth} y2={minY} stroke={THEME.foreground} strokeWidth={1.5} />
                <line x1={centerX - capWidth} y1={maxY} x2={centerX + capWidth} y2={maxY} stroke={THEME.foreground} strokeWidth={1.5} />
                <rect x={centerX - halfBox} y={q3Y} width={boxWidth} height={q1Y - q3Y} fill={color} stroke={THEME.foreground} strokeWidth={1.5} rx={THEME.barRadius} ry={THEME.barRadius} />
                <line x1={centerX - halfBox} y1={medianY} x2={centerX + halfBox} y2={medianY} stroke={THEME.foreground} strokeWidth={2} />
${outliersBlock}
              </g>
            );
          })}
        </Group>
      </svg>
    </div>
  );
}
`
  );
}
